import React from 'react';
import { Clip, Track } from '../../types';
import { colors, dimensions, TimelineMode } from '../../../theme';
import { ClipBlock } from './clip-block';
import { DragMode, DragSnapshot } from '../../hooks/use-clip-drag';
import { LayerContextMenu } from '../layer-context-menu';
import type { TrackRowClipEntry } from './track-area';

type TrackRowProps = {
  track: Track;
  clips: TrackRowClipEntry[];
  isSelected: boolean;
  selectedClipIds: Set<string> | null;
  mode: TimelineMode;
  pxPerSecond: number;
  startOffset: number;
  dragSnapshot: DragSnapshot | null;
  dragLinkedClipIds: Set<string> | null;
  onSelect: () => void;
  onClipSelect: (entry: TrackRowClipEntry) => void;
  onClipDuplicate: (entry: TrackRowClipEntry) => void;
  onClipDelete: (entry: TrackRowClipEntry) => void;
  onClipDragStart: (e: React.MouseEvent, clip: Clip, dragMode: DragMode) => void;
};

export const TrackRow: React.FC<TrackRowProps> = ({
  track,
  clips,
  isSelected,
  selectedClipIds,
  mode,
  pxPerSecond,
  startOffset,
  dragSnapshot,
  dragLinkedClipIds,
  onSelect,
  onClipSelect,
  onClipDuplicate,
  onClipDelete,
  onClipDragStart
}) => {
  const td = dimensions.timeline[mode];
  const lockDrag = track.locked;

  return (
    <div
      onClick={onSelect}
      style={{
        position: 'relative',
        height: td.rowHeight,
        minHeight: td.rowHeight,
        flexShrink: 0,
        backgroundColor: isSelected ? colors.rowSelected : colors.rowBg,
        cursor: 'default',
        overflow: 'hidden',
        transition: 'background-color 0.12s, height 0.2s ease, min-height 0.2s ease'
      }}
      onMouseEnter={e => {
        if (!isSelected) e.currentTarget.style.backgroundColor = colors.rowHover;
      }}
      onMouseLeave={e => {
        if (!isSelected) e.currentTarget.style.backgroundColor = colors.rowBg;
      }}
    >
      {clips.map(clipEntry => {
        const { clip, track: clipTrack, fabricObject, displayLabel, renderClip, previewClipId, resizable } = clipEntry;
        const dragClip = renderClip ?? clip;
        const activeClipId = previewClipId ?? clip.id;
        const isActiveDrag = dragSnapshot?.clipId === activeClipId;
        const isLinkedDrag =
          !!dragSnapshot &&
          dragSnapshot.mode !== 'resize-end' &&
          !isActiveDrag &&
          !!dragLinkedClipIds?.has(activeClipId);
        const clipDragSnapshot = isActiveDrag
          ? dragSnapshot
          : isLinkedDrag
            ? { clipId: activeClipId, mode: 'move' as DragMode, offsetPx: dragSnapshot.offsetPx, snapPx: null }
            : null;
        const isTextClip = clipTrack.kind === 'text';
        const clipBlock = (
          <ClipBlock
            clip={clip}
            trackKind={clipTrack.kind}
            renderClip={renderClip}
            previewClipId={previewClipId}
            pxPerSecond={pxPerSecond}
            isSelected={!!selectedClipIds && selectedClipIds.has(clip.id)}
            startOffset={startOffset}
            label={displayLabel ?? clipTrack.label}
            resizable={resizable}
            dragSnapshot={clipDragSnapshot}
            onContextMenu={isTextClip ? e => e.preventDefault() : undefined}
            onClick={e => {
              e.stopPropagation();
              onClipSelect({ track: clipTrack, clip, fabricObject });
            }}
            onDragStart={lockDrag ? undefined : (e, dragMode) => onClipDragStart(e, dragClip, dragMode)}
          />
        );

        return isTextClip ? (
          <React.Fragment key={clip.id}>{clipBlock}</React.Fragment>
        ) : (
          <LayerContextMenu
            key={clip.id}
            onDuplicate={() => onClipDuplicate({ track: clipTrack, clip, fabricObject })}
            onDelete={() => onClipDelete({ track: clipTrack, clip, fabricObject })}
            layerId={clipTrack.layerId}
          >
            {clipBlock}
          </LayerContextMenu>
        );
      })}
    </div>
  );
};
